import { Box, LoaderTalos, ModeEnum, Stack, Toasts, Z_INDEX } from '@talos/kyoko';
import { useEffect } from 'react';
import { useAppStateDispatch, useAppStateSelector } from '../../../../providers/AppStateProvider';
import { useGetCustomerQuotesQuery } from '../../../../providers/AppStateProvider/streamingDataSlice';
import { ManualRFQPricingContextProvider, useManualRFQPricingContext } from '../ManualRFQPricingContextProvider';
import { PricingPanelBlotter } from './PricingPanelBlotter';
import { PricingPanelBottomActions } from './PricingPanelBottomActions';
import { PricingPanelHeader } from './PricingPanelHeader';

export const ManualRFQPricingPanelContent = ({ rfqID }: { rfqID: string }) => {
  return (
    <ManualRFQPricingContextProvider instanceID={rfqID}>
      <ManualRFQPricingPanelContentInner rfqID={rfqID} />
    </ManualRFQPricingContextProvider>
  );
};

const ManualRFQPricingPanelContentInner = ({ rfqID }: { rfqID: string }) => {
  const quote = useGetCustomerQuotesQuery({ RFQID: rfqID }).data?.get(rfqID);

  const { selectors, actions, toasts } = useManualRFQPricingContext();
  const { initialized } = useAppStateSelector(selectors.selectState);

  const dispatch = useAppStateDispatch();

  // If we arrive here in the panel contents and the pricing slice is not instantiated, we need to prime with our quote
  // There are two ways of getting to the panel. 1 is via the priming thunk, used when clicking the Quote button in the Manual RFQs blotter
  // The second is if you just open the tab of this persisted panel. That is the case we are handling with this useEffect.
  useEffect(() => {
    if (!initialized && quote) {
      dispatch(actions.primeState(quote));
    }
  }, [dispatch, initialized, quote, actions]);

  if (!quote) {
    return <LoaderTalos />;
  }

  return (
    <Stack gap="spacing2" h="100%">
      <PricingPanelHeader quote={quote} />
      <Stack h="100%" gap="spacing2">
        <PricingPanelBlotter sizeCounteringEnabled={quote.NegotiationParameters?.SizeCountering === ModeEnum.Enabled} />
        <PricingPanelBottomActions quote={quote} />
      </Stack>

      {/* We render our own toasts specific to our manual rfq pricing panel here */}
      <Box
        position="absolute"
        py="spacing16"
        zIndex={Z_INDEX.globalToast}
        w="350px"
        left="50%"
        transform="translateX(-50%)" // centering
      >
        <Toasts toasts={toasts.toasts} remove={toasts.remove} />
      </Box>
    </Stack>
  );
};
